// Share permalink params (README §1.4/§3.1). Encoding: each active filter is
// `field=v1,v2`; chart controls use the reserved keys `y` (Y-axis field),
// `axis` (axis-switcher selection) and `view` (chart|table); `group` reuses the
// existing NvChart group deep-link. Params are produced only on Share click and
// consumed + stripped on load, so the address bar stays clean during use.
//
// All permalink controls are consumed on load and removed from the address bar
// so it stays clean during use (§1.4). `group` is read by NvChart before the
// strip runs; it's only ever written to the URL by Share, never live.
const STRIP_CONTROL_KEYS = ['group', 'y', 'axis', 'view'];

function currentParams() {
  return new URLSearchParams(window.location.search);
}

// Decode a single share value; tolerate a malformed %-escape from a hand-edited
// URL rather than throwing (parse runs on page load).
function decodeValue(raw) {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function getSharedParam(name) {
  return currentParams().get(name);
}

// Filter field names a group can pin: facet attributes plus each grouped
// dropdown's section fields. The axis switcher is a chart-axis control, encoded
// as `axis`, not a row filter — so it is excluded here.
export function filterFieldsOf(group) {
  const fields = [];
  (group?.filters || []).forEach(f => {
    if (f.kind === 'grouped-dropdown') {
      (f.sections || []).forEach(s => fields.push(s.field));
    } else if (f.kind !== 'axis-switcher') {
      fields.push(f.attribute);
    }
  });
  return fields.filter(Boolean);
}

export function buildShareUrl({ groupLabel, filters = [], yField, axisField, viewMode }) {
  const params = new URLSearchParams();
  if (groupLabel) params.set('group', groupLabel);
  filters.forEach(f => {
    const values = (f.values || []).filter(v => v != null && v !== '');
    // Encode each value so a comma inside a value (e.g. a model name "Llama 3, 8B")
    // survives the comma-delimited join — URLSearchParams round-trips commas
    // transparently, so an un-encoded value comma is indistinguishable from the
    // delimiter on parse. parseSharedFilters decodes each value back.
    if (values.length) params.set(f.field, values.map(v => encodeURIComponent(v)).join(','));
  });
  if (yField) params.set('y', yField);
  if (axisField) params.set('axis', axisField);
  if (viewMode && viewMode !== 'chart') params.set('view', viewMode);

  // Merge onto the existing query string instead of replacing it: clear only
  // the chart-owned keys (control keys + this group's filter fields, so a filter
  // cleared to empty doesn't linger) and set the new ones. Unrelated page params
  // — UTM / tracking tokens / anything else — survive in the shared URL.
  const url = new URL(window.location.href);
  [...STRIP_CONTROL_KEYS, ...filters.map(f => f.field)].forEach(key => url.searchParams.delete(key));
  params.forEach((value, key) => url.searchParams.set(key, value));
  return url.toString();
}

// Filter selections parsed from the URL for the given fields (search-ui shape).
export function parseSharedFilters(fields) {
  const params = currentParams();
  return fields.reduce((acc, field) => {
    const raw = params.get(field);
    // Each value was encodeURIComponent'd by buildShareUrl so value-commas survive the join.
    if (raw != null && raw !== '') acc.push({ field, values: raw.split(',').map(decodeValue), type: 'any' });
    return acc;
  }, []);
}

// Remove the controls + filters we own from the address bar — the control keys
// (`group`, `y`, `axis`, `view`; `group` has already been read by NvChart on
// load) plus this group's filter fields — leaving unrelated params (UTM,
// tracking tokens, …) intact. No reload.
export function stripSharedParams(fields) {
  const url = new URL(window.location.href);
  [...STRIP_CONTROL_KEYS, ...fields].forEach(key => url.searchParams.delete(key));
  window.history.replaceState({}, '', url.toString());
}
